import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Card, CardContent, CircularProgress, Table, Typography } from '@mui/joy';
import { Link, useSearchParams } from 'react-router-dom';
import { directSupabase } from '../lib/supabaseClient';

type DrilldownMode = 'estimated' | 'accepted';

type AdditionalCostRow = {
  id: string;
  documentId: string;
  lifecycleRef: string;
  title: string;
  customerName: string;
  additionalType: string;
  name: string;
  qty: number;
  unit: string;
  unitPrice: number;
  lineTotal: number;
  vatInclusive: boolean;
  createdAt: string | null;
  acceptedAt: string | null;
};

const getTypeLabel = (type: string): string => {
  const labels: Record<string, string> = {
    glass: 'Glass',
    window: 'Window(s)',
    door: 'Door(s)',
    window_door: 'Window(s) & Door(s)',
    window_glass: 'Window(s) & Glass',
    door_glass: 'Door(s) & Glass',
    other: 'Other',
    unspecified: 'Unspecified',
    all: 'All Types'
  };
  return labels[type] || type;
};

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(Number(value || 0));

const AdditionalCostsDrilldownPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<AdditionalCostRow[]>([]);

  const mode: DrilldownMode = searchParams.get('mode') === 'accepted' ? 'accepted' : 'estimated';
  const typeFilterRaw = String(searchParams.get('type') || 'all').trim() || 'all';
  const typeFilters = typeFilterRaw === 'all'
    ? ['all']
    : typeFilterRaw
        .split(',')
        .map((value) => String(value || '').trim())
        .filter(Boolean);
  const from = searchParams.get('from');
  const to = searchParams.get('to');
  const period = searchParams.get('period') || '';

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        setLoading(true);
        setError(null);

        const { data: docs, error: docsError } = await directSupabase
          .from('documents')
          .select('id, kind, status, title, lifecycle_ref, organisation_id, customer_first_name, customer_last_name, active_version_id, created_at, accepted_at')
          .in('kind', ['Quotation', 'Estimate'])
          .neq('status', 'void');

        if (docsError) throw new Error(docsError.message || 'Failed to load documents');

        const rangeFromTs = from ? new Date(from).getTime() : null;
        const rangeToTs = to ? new Date(to).getTime() : null;
        const toTs = (value: any): number | null => {
          if (!value) return null;
          const ts = new Date(value).getTime();
          return Number.isFinite(ts) ? ts : null;
        };
        const inRangeTs = (value: any): boolean => {
          const ts = toTs(value);
          if (ts == null) return false;
          if (rangeFromTs != null && ts < rangeFromTs) return false;
          if (rangeToTs != null && ts >= rangeToTs) return false;
          return true;
        };

        const quoteEstimateByLifecycle = new Map<string, any>();
        (docs || []).forEach((row: any) => {
          const lifecycleRef = String(row.lifecycle_ref || '').trim();
          const orgId = String(row.organisation_id || '').trim();
          const key = lifecycleRef ? `${orgId}:${lifecycleRef}` : `doc:${String(row.id || '').trim()}`;
          const existing = quoteEstimateByLifecycle.get(key);
          if (!existing) {
            quoteEstimateByLifecycle.set(key, row);
            return;
          }
          const existingTs = new Date(existing.created_at || 0).getTime();
          const currentTs = new Date(row.created_at || 0).getTime();
          if (currentTs >= existingTs) quoteEstimateByLifecycle.set(key, row);
        });

        const dedupedDocs = Array.from(quoteEstimateByLifecycle.values());
        const filteredDocs = dedupedDocs.filter((doc: any) => {
          if (mode === 'accepted') {
            return String(doc.status || '').toLowerCase() === 'accepted' && inRangeTs(doc.accepted_at);
          }
          return inRangeTs(doc.created_at);
        });

        const byVersionId = new Map<string, any>();
        filteredDocs.forEach((doc: any) => {
          const versionId = String(doc.active_version_id || '').trim();
          if (versionId) byVersionId.set(versionId, doc);
        });
        const versionIds = Array.from(byVersionId.keys());
        if (versionIds.length === 0) {
          if (!alive) return;
          setRows([]);
          return;
        }

        const { data: lineRows, error: lineError } = await directSupabase
          .from('document_version_lines')
          .select('id, version_id, line_kind, additional_type, name, qty, unit, unit_price, line_total, vat_inclusive, meta_json')
          .in('version_id', versionIds)
          .eq('line_kind', 'other');
        if (lineError) throw new Error(lineError.message || 'Failed to load additional cost lines');

        const parseMeta = (value: any) => {
          if (!value) return null;
          if (typeof value === 'object') return value;
          if (typeof value === 'string') {
            try {
              return JSON.parse(value);
            } catch {
              return null;
            }
          }
          return null;
        };

        const nextRows: AdditionalCostRow[] = [];
        (lineRows || []).forEach((line: any, idx: number) => {
          const sourceDoc = byVersionId.get(String(line.version_id || '').trim());
          if (!sourceDoc) return;
          const meta = parseMeta(line.meta_json);
          if (String(meta?.feature || '').trim() === 'visual_designer') return;

          const normalizedType = String(line.additional_type || '').trim() || 'unspecified';
          if (!(typeFilters.includes('all') || typeFilters.includes(normalizedType))) return;

          const qty = Number(line.qty ?? 0);
          const unitPrice = Number(line.unit_price ?? 0);
          const computedLineTotal = line.line_total != null ? Number(line.line_total) : qty * unitPrice;
          const customerName = `${String(sourceDoc.customer_first_name || '').trim()} ${String(sourceDoc.customer_last_name || '').trim()}`.trim() || '—';

          nextRows.push({
            id: String(line.id || `${sourceDoc.id}-${idx}`),
            documentId: String(sourceDoc.id || ''),
            lifecycleRef: String(sourceDoc.lifecycle_ref || '').trim() || String(sourceDoc.id || '').slice(0, 8).toUpperCase(),
            title: String(sourceDoc.title || '').trim() || 'Quote/Estimate',
            customerName,
            additionalType: normalizedType,
            name: String(line.name || '').trim() || '—',
            qty: Number.isFinite(qty) ? qty : 0,
            unit: String(line.unit || '').trim(),
            unitPrice: Number.isFinite(unitPrice) ? unitPrice : 0,
            lineTotal: Number.isFinite(computedLineTotal) ? computedLineTotal : 0,
            vatInclusive: !!line.vat_inclusive,
            createdAt: sourceDoc.created_at || null,
            acceptedAt: sourceDoc.accepted_at || null,
          });
        });

        nextRows.sort((a, b) => {
          const aTs = new Date((mode === 'accepted' ? a.acceptedAt : a.createdAt) || 0).getTime();
          const bTs = new Date((mode === 'accepted' ? b.acceptedAt : b.createdAt) || 0).getTime();
          return bTs - aTs;
        });

        if (!alive) return;
        setRows(nextRows);
      } catch (err: any) {
        if (!alive) return;
        setError(err?.message || 'Failed to load additional cost drilldown');
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    return () => {
      alive = false;
    };
  }, [from, to, mode, typeFilterRaw]);
  const typeFilterLabel = useMemo(() => {
    if (typeFilters.includes('all')) return 'All Types';
    return typeFilters.map((value) => getTypeLabel(value)).join(', ');
  }, [typeFilterRaw]);


  const totalValue = useMemo(
    () => rows.reduce((sum, row) => sum + Number(row.lineTotal || 0), 0),
    [rows]
  );

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 320 }}>
        <CircularProgress size="lg" />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography level="h2" sx={{ mb: 1 }}>
        Additional Costs Drilldown
      </Typography>
      <Typography level="body-sm" sx={{ color: 'text.secondary', mb: 2 }}>
        Scope: {mode === 'accepted' ? 'Accepted quote/estimate lines (accepted_at gated)' : 'Estimated quote/estimate lines (created_at gated)'}
        {period ? ` • Period: ${period}` : ''}
        {` • Type: ${typeFilterLabel}`}
      </Typography>

      {error && (
        <Alert color="danger" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Typography level="title-md">Summary</Typography>
          <Typography level="body-sm">Matching line items: {rows.length}</Typography>
          <Typography level="body-sm">Total additional costs: {formatCurrency(totalValue)}</Typography>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Typography level="title-md" sx={{ mb: 1.5 }}>Matching Additional Cost Lines</Typography>
          {rows.length === 0 ? (
            <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
              No results found for this filter.
            </Typography>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table hoverRow>
                <thead>
                  <tr>
                    <th>Doc Ref</th>
                    <th>Customer</th>
                    <th>Type</th>
                    <th>Name</th>
                    <th>Qty</th>
                    <th>Unit</th>
                    <th>Unit Price</th>
                    <th>Line Total</th>
                    <th>VAT Inclusive</th>
                    <th>{mode === 'accepted' ? 'Accepted At' : 'Created At'}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.id}>
                      <td>
                        <Link to={`/docs/${row.documentId}`}>{row.lifecycleRef}</Link>
                      </td>
                      <td>{row.customerName}</td>
                      <td>{getTypeLabel(row.additionalType)}</td>
                      <td>{row.name}</td>
                      <td>{row.qty}</td>
                      <td>{row.unit || '—'}</td>
                      <td>{formatCurrency(row.unitPrice)}</td>
                      <td>{formatCurrency(row.lineTotal)}</td>
                      <td>{row.vatInclusive ? 'Yes' : 'No'}</td>
                      <td>
                        {(() => {
                          const dateValue = mode === 'accepted' ? row.acceptedAt : row.createdAt;
                          if (!dateValue) return '—';
                          const d = new Date(dateValue);
                          return Number.isFinite(d.getTime()) ? d.toLocaleDateString('en-GB') : '—';
                        })()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default AdditionalCostsDrilldownPage;
